import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface MenuItemConfig {
  href: string;
  label: string;
  section: string;
  visible: boolean;
  custom?: boolean;
}

const DEFAULT_ITEMS: MenuItemConfig[] = [
  { href: '/dashboard',      label: 'Dashboard',     section: 'Operations', visible: true },
  { href: '/payments',       label: 'Transactions',  section: 'Operations', visible: true },
  { href: '/refunds',        label: 'Refunds',       section: 'Operations', visible: true },
  { href: '/recon',          label: 'Reconciliation', section: 'Operations', visible: true },
  { href: '/analytics',      label: 'Analytics',     section: 'Operations', visible: true },
  { href: '/mandates',       label: 'Mandates',      section: 'Payments',   visible: true },
  { href: '/tokenization',   label: 'Tokens',        section: 'Payments',   visible: true },
  { href: '/gateway-config', label: 'Gateways',      section: 'Config',     visible: true },
  { href: '/routing',        label: 'Smart Routing', section: 'Config',     visible: true },
  { href: '/users',          label: 'Users',          section: 'Admin',    visible: true },
  ...(import.meta.env.PROD ? [] : [{ href: '/load-tester', label: 'Load Tester', section: 'Admin', visible: true }]),
  { href: '/payment-config', label: 'Payment Config', section: 'Admin',    visible: true },
];

interface MenuState {
  items: MenuItemConfig[];
  toggleVisible: (href: string) => void;
  moveUp: (href: string) => void;
  moveDown: (href: string) => void;
  addItem: (item: Omit<MenuItemConfig, 'visible' | 'custom'>) => void;
  removeItem: (href: string) => void;
  updateLabel: (href: string, label: string) => void;
  reset: () => void;
}

export const useMenuStore = create<MenuState>()(
  persist(
    (set) => ({
      items: DEFAULT_ITEMS,
      toggleVisible: (href) => set((s) => ({
        items: s.items.map((i) => i.href === href ? { ...i, visible: !i.visible } : i),
      })),
      moveUp: (href) => set((s) => {
        const items = [...s.items];
        const idx = items.findIndex((i) => i.href === href);
        if (idx <= 0) return s;
        [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
        return { items };
      }),
      moveDown: (href) => set((s) => {
        const items = [...s.items];
        const idx = items.findIndex((i) => i.href === href);
        if (idx < 0 || idx >= items.length - 1) return s;
        [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
        return { items };
      }),
      addItem: (item) => set((s) => ({
        items: [...s.items, { ...item, visible: true, custom: true }],
      })),
      removeItem: (href) => set((s) => ({
        items: s.items.filter((i) => i.href !== href),
      })),
      updateLabel: (href, label) => set((s) => ({
        items: s.items.map((i) => i.href === href ? { ...i, label } : i),
      })),
      reset: () => set({ items: DEFAULT_ITEMS }),
    }),
    {
      name: 'sa-menu-config-v3',
      // Union new DEFAULT_ITEMS into a previously persisted menu so items
      // shipped after the user first saved their config still show up.
      merge: (persisted, current) => {
        const p = persisted as Partial<MenuState> | undefined;
        if (!p?.items) return current;
        const existing = new Set(p.items.map((i) => i.href));
        const missing = DEFAULT_ITEMS.filter((d) => !existing.has(d.href));
        return { ...current, ...p, items: [...p.items, ...missing] };
      },
    }
  )
);
